import { useCallback } from 'react';
import request from '../utils/request';

export interface PagedView<T> {
    currentPage?: number;
    lastPage?: number;
    perPage?: number;
    total_pages?: number;
    total_elements: number;
    content: T[];
}

export interface DictionaryDto {
    id: string | number;
    code: string;
    description: string;
    status: string;
    relog_api_key?: string;
    relog_enabled?: boolean;
}

export interface LabelValue {
    label: string;
    value: string;
}

export function initDictItem(): DictionaryDto {
    return {
        status: '',
        description: '',
        code: '',
        id: '',
    };
}

export function useContentApi<TEntity>(baseUrl: string) {
    const fetchAll = useCallback((): Promise<TEntity[]> => {
        return request
            .get(baseUrl, {
                params: {
                    page: 0,
                    size: 1000,
                },
            })
            .then(({ data }) => data as PagedView<TEntity>)
            .then((res) => res.content);
    }, [baseUrl]);

    return { fetchAll };
}

export function useGetApi<TEntity>(baseUrl: string) {
    const fetchAll = useCallback(
        (page: number, size: number, sort: string, value: string | null): Promise<TEntity[]> => {
            return request
                .get(baseUrl, { params: { page, size, sort, value } })
                .then(({ data }) => data as TEntity[]);
        },
        [baseUrl]
    );

    return { fetchAll };
}

export function useDictionaryApi<TEntity>(baseUrl: string) {
    const getPaged = useCallback(
        (
            page: number,
            size: number,
            sort: string,
            value: string | null
        ): Promise<PagedView<TEntity>> => {
            return request
                .get(baseUrl, { params: { page, size, sort, value } })
                .then(({ data }) => data as PagedView<TEntity>);
        },
        [baseUrl]
    );

    const getById = useCallback(
        (id: string): Promise<TEntity> => {
            return request.get(`${baseUrl}/${id}`).then(({ data }) => data);
        },
        [baseUrl]
    );

    const post = useCallback(
        (entity: TEntity) =>
            request({
                url: baseUrl,
                method: 'POST',
                data: entity,
            }),
        [baseUrl]
    );

    const put = useCallback(
        (entity: TEntity) =>
            request({
                url: baseUrl,
                method: 'PUT',
                data: entity,
            }),
        [baseUrl]
    );

    return {
        getPaged,
        post,
        put,
        getById,
    };
}
